import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import TopBar from '../components/TopBar';
import AssignTemplateModal from '../components/AssignTemplateModal';
import CalendarEventTooltip from '../components/CalendarEventTooltip';
import scheduleAssignmentService from '../services/scheduleAssignmentService';
import channelService from '../services/channelService';
import type {
  ScheduleAssignmentWithDetails,
  AssignmentStatus,
  ListAssignmentsParams,
} from '../services/scheduleAssignmentService';
import type { Channel } from '../services/channelService';

// Setup the localizer for react-big-calendar
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

const SchedulesPage: React.FC = () => {
  const [assignments, setAssignments] = useState<ScheduleAssignmentWithDetails[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [preselectedChannelId, setPreselectedChannelId] = useState<string | undefined>(undefined);
  const [preselectedStartTime, setPreselectedStartTime] = useState<string | undefined>(undefined);

  // View mode
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');
  
  // Calendar refresh key - incrementing this forces calendar to re-mount with fresh data
  const [calendarKey, setCalendarKey] = useState(0);

  // Filters
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<AssignmentStatus | 'all'>('all');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(() => {
    // Default to today's assignments
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekLater = new Date(today);
    weekLater.setDate(weekLater.getDate() + 7); // Next 7 days
    return {
      start: today.toISOString(),
      end: weekLater.toISOString(),
    };
  });
  const [sortBy, setSortBy] = useState<'start_time' | 'created_at'>('start_time');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Pagination (for list view)
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    scheduled: 0,
    playing: 0,
    completed: 0,
    failed: 0,
  });

  // Load channels
  const loadChannels = async () => {
    try {
      const response = await channelService.listChannels({ limit: 100 });
      setChannels(response.channels);
      if ((channelFilter === 'all' || !channelFilter) && response.channels.length > 0) {
        setChannelFilter(response.channels[0].id);
      }
    } catch (err) {
      console.error('Error loading channels:', err);
    }
  };

  // Load assignments
  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListAssignmentsParams = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
        start_date: dateRange.start,
        end_date: dateRange.end,
      };

      if (channelFilter !== 'all') {
        params.channel_id = channelFilter;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await scheduleAssignmentService.listAssignments(params);
      setAssignments(response.assignments);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load assignments');
      console.error('Error loading assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const data = await scheduleAssignmentService.getStats(channelFilter !== 'all' ? channelFilter : undefined);
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  // Initial load
  useEffect(() => {
    loadChannels();
  }, []);

  useEffect(() => {
    loadAssignments();
    loadStats();
  }, [page, channelFilter, statusFilter, dateRange, sortBy, sortOrder]);

  // Handle mark as playing
  const handleMarkPlaying = async (assignment: ScheduleAssignmentWithDetails) => {
    try {
      await scheduleAssignmentService.markAsPlaying(assignment.id);
      loadAssignments();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to mark as playing');
    }
  };

  // Handle mark as completed
  const handleMarkCompleted = async (assignment: ScheduleAssignmentWithDetails) => {
    try {
      await scheduleAssignmentService.markAsCompleted(assignment.id);
      loadAssignments();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to mark as completed');
    }
  };

  // Handle delete
  const handleDelete = async (assignment: ScheduleAssignmentWithDetails) => {
    if (!window.confirm(`Are you sure you want to delete this assignment?`)) {
      return;
    }

    try {
      await scheduleAssignmentService.deleteAssignment(assignment.id);
      loadAssignments();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete assignment');
    }
  };

  // Handle create assignment
  const handleCreateAssignment = (channelId?: string, startTime?: string) => {
    setPreselectedChannelId(channelId);
    setPreselectedStartTime(startTime);
    setShowAssignModal(true);
  };

  // Helper function to snap time to nearest 30-minute interval
  const snapToHalfHour = (date: Date): Date => {
    const snapped = new Date(date);
    const minutes = snapped.getMinutes();
    const remainder = minutes % 30;
    
    if (remainder < 15) {
      // Round down
      snapped.setMinutes(minutes - remainder);
    } else {
      // Round up
      snapped.setMinutes(minutes + (30 - remainder));
    }
    snapped.setSeconds(0);
    snapped.setMilliseconds(0);
    return snapped;
  };

  // Find the best snap time for a given slot click
  const findBestSnapTime = (clickedTime: Date, channelId?: string): Date => {
    // Get assignments for the clicked date on the relevant channel
    const dateStr = clickedTime.toISOString().split('T')[0];
    const relevantAssignments = assignments.filter(a => {
      const assignmentDate = new Date(a.start_time).toISOString().split('T')[0];
      const sameDay = assignmentDate === dateStr;
      const sameChannel = !channelId || a.channel_id === channelId;
      return sameDay && sameChannel;
    });

    if (relevantAssignments.length === 0) {
      return snapToHalfHour(clickedTime);
    }

    // Find program ends that are close to the clicked time
    const programEnds = relevantAssignments.map(a => new Date(a.end_time));
    
    // Find the closest program end
    let closestEnd: Date | null = null;
    let closestDiff = Infinity;
    
    for (const end of programEnds) {
      const diff = Math.abs(clickedTime.getTime() - end.getTime());
      if (diff < closestDiff) {
        closestDiff = diff;
        closestEnd = end;
      }
    }

    // If within 1 hour of a program end, snap to it
    const oneHourMs = 60 * 60 * 1000;
    if (closestEnd && closestDiff < oneHourMs) {
      return closestEnd;
    }

    // Otherwise snap to nearest 30-minute interval
    return snapToHalfHour(clickedTime);
  };

  // Handle calendar slot selection with snap-to feature
  const handleSelectSlot = (slotInfo: any) => {
    if (channelFilter === 'all') {
      alert('Select a specific channel from the dropdown to schedule using the calendar.');
      return;
    }
    const clickedTime = new Date(slotInfo.start);
    const selectedChannelId = channelFilter !== 'all' ? channelFilter : undefined;
    const snappedTime = findBestSnapTime(clickedTime, selectedChannelId);

    handleCreateAssignment(selectedChannelId, snappedTime.toISOString());
  };

  // Handle assignment creation success
  const handleAssignmentCreated = () => {
    loadAssignments();
    loadStats();
  };

  // Handle event drop (drag and drop reschedule) with snap-to feature
  const handleEventDrop = async ({ event, start }: any) => {
    const assignment: ScheduleAssignmentWithDetails = event.resource;

    // Only allow rescheduling of scheduled assignments
    if (assignment.status !== 'scheduled') {
      alert('Only scheduled assignments can be rescheduled. This assignment is ' + assignment.status);
      return;
    }

    const droppedTime = new Date(start);
    const eventDuration = new Date(assignment.end_time).getTime() - new Date(assignment.start_time).getTime();
    const proposedEndTime = new Date(droppedTime.getTime() + eventDuration);
    
    // Get other assignments on the same channel (excluding the current one)
    const otherAssignments = assignments.filter(a => 
      a.id !== assignment.id && a.channel_id === assignment.channel_id
    );

    let snappedTime: Date = snapToHalfHour(droppedTime);
    
    if (otherAssignments.length > 0) {
      // Check for any overlapping assignments
      const overlappingBefore = otherAssignments.filter(a => {
        const aStart = new Date(a.start_time);
        const aEnd = new Date(a.end_time);
        // Assignment ends after our proposed start but starts before our proposed start
        return aEnd > droppedTime && aStart < droppedTime;
      });

      const overlappingAfter = otherAssignments.filter(a => {
        const aStart = new Date(a.start_time);
        // Assignment starts before our proposed end but after our proposed start
        return aStart < proposedEndTime && aStart >= droppedTime;
      });

      if (overlappingBefore.length > 0) {
        // Snap to the end of the overlapping assignment (start right after it)
        const latestOverlapEnd = overlappingBefore.reduce((latest, a) => {
          const endTime = new Date(a.end_time);
          return endTime > latest ? endTime : latest;
        }, new Date(0));
        snappedTime = latestOverlapEnd;
      } else if (overlappingAfter.length > 0) {
        // There's something starting during our proposed time, snap to end just before it
        const earliestNextStart = overlappingAfter.reduce((earliest, a) => {
          const startTime = new Date(a.start_time);
          return startTime < earliest ? startTime : earliest;
        }, new Date(8640000000000000));
        
        // Calculate when we should start to end just before the next assignment
        const shouldStartAt = new Date(earliestNextStart.getTime() - eventDuration);
        snappedTime = shouldStartAt > droppedTime ? snapToHalfHour(droppedTime) : shouldStartAt;
      } else {
        // No overlap, find the closest program end to snap to if nearby
        const programEnds = otherAssignments.map(a => new Date(a.end_time));
        const closestEnd = programEnds.reduce((closest, end) => {
          const currentDiff = Math.abs(droppedTime.getTime() - closest.getTime());
          const newDiff = Math.abs(droppedTime.getTime() - end.getTime());
          return newDiff < currentDiff ? end : closest;
        }, new Date(0));

        // If within 30 minutes of a program end, snap to it
        const thirtyMinMs = 30 * 60 * 1000;
        if (Math.abs(droppedTime.getTime() - closestEnd.getTime()) < thirtyMinMs) {
          snappedTime = closestEnd;
        }
      }
    }

    try {
      // Update the assignment with snapped start time
      await scheduleAssignmentService.reschedule(assignment.id, snappedTime.toISOString());
      await loadAssignments();
      loadStats();
      // Force calendar to re-mount with fresh data
      setCalendarKey(k => k + 1);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to reschedule');
      await loadAssignments(); // Reload to revert visual change
      setCalendarKey(k => k + 1);
    }
  };

  // Handle event resize (change duration by changing template - not supported directly)
  const handleEventResize = async () => {
    alert('To change assignment duration, edit the template or select a different template. Duration is auto-calculated from template videos.');
    await loadAssignments(); // Reload to revert visual change
    setCalendarKey(k => k + 1);
  };

  // Reset filters
  const resetFilters = () => {
    setChannelFilter('all');
    setStatusFilter('all');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekLater = new Date(today);
    weekLater.setDate(weekLater.getDate() + 7);
    setDateRange({
      start: today.toISOString(),
      end: weekLater.toISOString(),
    });
    setSortBy('start_time');
    setSortOrder('asc');
    setPage(1);
  };

  // Calendar events
  const calendarEvents = assignments.map((assignment) => ({
    id: assignment.id,
    title: assignment.template_name || 'Unknown Template',
    start: new Date(assignment.start_time),
    end: new Date(assignment.end_time),
    resource: assignment,
  }));

  // Custom event formats for calendar
  const calendarFormats = {
    eventTimeRangeFormat: () => '', // Hide time range, show only title
  };

  // Event style getter for calendar
  const eventStyleGetter = (event: any) => {
    const assignment: ScheduleAssignmentWithDetails = event.resource;
    let backgroundColor = '#3174ad';

    switch (assignment.status) {
      case 'scheduled':
        backgroundColor = '#3b82f6';
        break;
      case 'playing':
        backgroundColor = '#10b981';
        break;
      case 'completed':
        backgroundColor = '#6b7280';
        break;
      case 'failed':
        backgroundColor = '#ef4444';
        break;
      case 'skipped':
        backgroundColor = '#f59e0b';
        break;
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    };
  };

  // Custom event component wrapper for calendar
  const EventComponent = ({ event }: any) => (
    <CalendarEventTooltip event={event} onDelete={handleDelete} />
  );

  // Calendar components configuration
  const calendarComponents = {
    event: EventComponent,
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  };

  const activeChannel = channelFilter !== 'all'
    ? channels.find((channel) => channel.id === channelFilter)
    : null;
  const isChannelSelected = Boolean(activeChannel);

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Calendar</h1>
            <p className="text-gray-600 mt-1">Manage schedule assignments on channels</p>
          </div>
          <button
            onClick={() => handleCreateAssignment()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2.5 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Assign Template
          </button>
        </div>

        {/* Stats Dashboard */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-blue-200">
            <p className="text-sm text-blue-600 mb-1">Scheduled</p>
            <p className="text-2xl font-bold text-blue-700">{stats.scheduled}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-green-200">
            <p className="text-sm text-green-600 mb-1">Playing</p>
            <p className="text-2xl font-bold text-green-700">{stats.playing}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Completed</p>
            <p className="text-2xl font-bold text-gray-700">{stats.completed}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-red-200">
            <p className="text-sm text-red-600 mb-1">Failed</p>
            <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
          </div>
        </div>

        {/* View Toggle and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* View Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
                List
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  viewMode === 'calendar'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                Calendar
              </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Channel Filter */}
              <select
                value={channelFilter}
                onChange={(e) => {
                  setChannelFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Channels</option>
                {channels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.name}{channel.loop_last_template ? ' (Looping)' : ''}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as AssignmentStatus | 'all');
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="scheduled">Scheduled</option>
                <option value="playing">Playing</option>
                <option value="completed">Completed</option>
                <option value="skipped">Skipped</option>
                <option value="failed">Failed</option>
              </select>

              {/* Sort By */}
              {viewMode === 'list' && (
                <>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="start_time">Start Time</option>
                    <option value="created_at">Created</option>
                  </select>

                  {/* Sort Order */}
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </>
              )}

              {/* Reset Filters */}
              <button
                onClick={resetFilters}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Selected Channel Info */}
        {activeChannel && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200 flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Viewing channel:</span>
              <span className="font-semibold text-gray-900">{activeChannel.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Loop last template:</span>
              <span
                className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  activeChannel.loop_last_template
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {activeChannel.loop_last_template ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* List View */}
        {!loading && viewMode === 'list' && (
          <>
            {assignments.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center border border-gray-200">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No assignments found</h3>
                <p className="text-gray-600 mb-4">
                  {channelFilter !== 'all' || statusFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Create your first assignment to get started'}
                </p>
                <button
                  onClick={() => handleCreateAssignment()}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition-colors"
                >
                  Assign Template
                </button>
              </div>
            ) : (
              <>
                {/* Assignment Cards Grid */}
                <div className="space-y-4 mb-6">
                  {assignments.map((assignment) => (
                    <div
                      key={assignment.id}
                      className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{assignment.template_name}</h3>
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                assignment.status === 'scheduled'
                                  ? 'bg-blue-100 text-blue-700'
                                  : assignment.status === 'playing'
                                  ? 'bg-green-100 text-green-700'
                                  : assignment.status === 'completed'
                                  ? 'bg-gray-100 text-gray-700'
                                  : assignment.status === 'failed'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}
                            >
                              {assignment.status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p>
                              <span className="font-medium">Channel:</span> {assignment.channel_name}
                            </p>
                            <p>
                              <span className="font-medium">Start:</span> {new Date(assignment.start_time).toLocaleString()}
                            </p>
                            <p>
                              <span className="font-medium">End:</span> {new Date(assignment.end_time).toLocaleString()}
                            </p>
                            <p>
                              <span className="font-medium">Duration:</span> {formatDuration(
                                Math.floor((new Date(assignment.end_time).getTime() - new Date(assignment.start_time).getTime()) / 1000)
                              )}
                            </p>
                            {assignment.notes && (
                              <p className="text-gray-500 italic mt-2">{assignment.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {assignment.status === 'scheduled' && (
                            <button
                              onClick={() => handleMarkPlaying(assignment)}
                              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                            >
                              Play
                            </button>
                          )}
                          {assignment.status === 'playing' && (
                            <button
                              onClick={() => handleMarkCompleted(assignment)}
                              className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded transition-colors"
                            >
                              Complete
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(assignment)}
                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                    <div className="text-sm text-gray-600">
                      Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} assignments
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <div className="flex items-center gap-2">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (page <= 3) {
                            pageNum = i + 1;
                          } else if (page >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = page - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPage(pageNum)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                page === pageNum
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Calendar View */}
        {!loading && viewMode === 'calendar' && isChannelSelected && (
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200" style={{ height: '700px' }}>
            {/* @ts-ignore - Calendar component type definitions issue */}
            <DnDCalendar
              key={calendarKey}
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              titleAccessor="title"
              style={{ height: '100%' }}
              defaultView={Views.DAY}
              views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
              formats={calendarFormats}
              eventPropGetter={eventStyleGetter}
              components={calendarComponents}
              onSelectSlot={handleSelectSlot}
              onEventDrop={handleEventDrop}
              onEventResize={handleEventResize}
              resizable={false} // Disable resize since duration is auto-calculated from template
              selectable
              popup
              draggableAccessor={(event: any) => {
                // Only allow dragging scheduled assignments
                const assignment: ScheduleAssignmentWithDetails = event.resource;
                return assignment.status === 'scheduled';
              }}
            />
          </div>
        )}

        {!loading && viewMode === 'calendar' && !isChannelSelected && (
          <div className="bg-white rounded-lg shadow-sm p-8 border border-dashed border-blue-200 text-center text-gray-600">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Channel</h3>
            <p className="mb-4">
              The calendar shows schedules for one channel at a time. Choose a channel from the filter above to view and edit its programming.
            </p>
            <button
              onClick={() => {
                if (channels.length > 0) {
                  setChannelFilter(channels[0].id);
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Use first channel
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-7-7l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Assign Template Modal */}
        <AssignTemplateModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onSuccess={handleAssignmentCreated}
          preselectedChannelId={preselectedChannelId}
          preselectedStartTime={preselectedStartTime}
        />
      </div>
    </div>
  );
};

export default SchedulesPage;
